// src/components/CalendarView/CalendarMonthView.tsx
import React, { useMemo } from 'react';
import type { CalendarEvent, ActionMenuConfig } from './types';
import { CalendarEventCard } from './CalendarEventCard';
import { getCalendarEventKey } from './calendarUtils';

const DAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function toDateKey(y: number, m: number, d: number): string
{
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function getEventDateKey(event: CalendarEvent): string
{
  const d = new Date(event.Date);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface CalendarMonthViewProps {
  events: CalendarEvent[];
  currentDate: Date;
  isLoading: boolean;
  onOpenInfoWindow: (conversationId: number) => void;
  actionMenuConfig: ActionMenuConfig;
  onDuplicateItem?: (e: any, conv: any) => void;
}

export const CalendarMonthView: React.FC<CalendarMonthViewProps> = ({
  events,
  currentDate,
  isLoading,
  onOpenInfoWindow,
  actionMenuConfig,
  onDuplicateItem,
}) =>
{
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const eventMap = useMemo(() =>
  {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach(ev =>
    {
      const key = getEventDateKey(ev);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    });
    return map;
  }, [events]);

  const days = useMemo(() =>
  {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const start = new Date(firstDay);
    start.setDate(start.getDate() - start.getDay());

    const end = new Date(lastDay);
    end.setDate(end.getDate() + (6 - end.getDay()));

    const result: { date: Date; isCurrentMonth: boolean; isToday: boolean }[] = [];
    const today = new Date();
    const todayKey = toDateKey(today.getFullYear(), today.getMonth(), today.getDate());

    const cur = new Date(start);
    while (cur <= end)
    {
      const key = toDateKey(cur.getFullYear(), cur.getMonth(), cur.getDate());
      result.push({
        date: new Date(cur),
        isCurrentMonth: cur.getMonth() === month,
        isToday: key === todayKey,
      });
      cur.setDate(cur.getDate() + 1);
    }
    return result;
  }, [year, month]);

  return (
    <div className="cal-month-grid">
      {DAY_LABELS.map((label, i) => (
        <div key={label} className={`cal-day-header${i === 0 || i === 6 ? ' is-weekend' : ''}`}>
          {label}
        </div>
      ))}
      {days.map(({ date, isCurrentMonth, isToday }) =>
      {
        const key = toDateKey(date.getFullYear(), date.getMonth(), date.getDate());
        const dayEvents = isLoading ? [] : eventMap.get(key) || [];
        const dow = date.getDay();
        const cellClasses = [
          'cal-day-cell',
          !isCurrentMonth ? 'cal-day-other-month' : '',
          isToday ? 'cal-day-today' : '',
          dow === 0 || dow === 6 ? 'is-weekend' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div key={key} className={cellClasses}>
            <div className="cal-day-number">{date.getDate()}</div>
            {isLoading ? (
              isCurrentMonth ? (
                <div className="cal-skeleton cal-skeleton-card" />
              ) : null
            ) : (
              dayEvents.map(ev => (
                <CalendarEventCard
                  key={getCalendarEventKey(ev)}
                  event={ev}
                  onOpenInfoWindow={onOpenInfoWindow}
                  actionMenuConfig={actionMenuConfig}
                  onDuplicateItem={onDuplicateItem}
                />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CalendarMonthView;
